import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Inject, Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { SHARED_ENVIRONMENT, SharedEnvironment } from '@sd-portal/shared/environment';
import { SessionStorageKeys } from '@sd-portal/shared/models';
import * as Sentry from '@sentry/browser';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import 'navigator.locks';
import { from, Observable, of, throwError } from 'rxjs';
import { catchError, switchMap, take } from 'rxjs/operators';

@Injectable()
export class AuthHttpInterceptor implements HttpInterceptor {
  private oidcSecurityService: OidcSecurityService;
  private readonly REFRESH_LOCK_NAME = 'auth-token-refresh';

  constructor(
    private readonly injector: Injector,
    private readonly router: Router,
    @Inject(SHARED_ENVIRONMENT) private readonly environment: SharedEnvironment
  ) {
  }

  intercept(
    req: HttpRequest<object>,
    next: HttpHandler
  ): Observable<HttpEvent<object>> {
    if (this.oidcSecurityService === undefined) {
      this.oidcSecurityService = this.injector.get(OidcSecurityService);
    }

    if (this.oidcSecurityService === undefined) {
      return next.handle(req);
    }

    return this.oidcSecurityService.getAccessToken().pipe(
      switchMap(token => {
        const requestToForward = this.addAuthHeader(req, token);

        return next.handle(requestToForward).pipe(
          catchError((error: HttpErrorResponse) => {
            if (error.status === 401) {
              return this.handle401Error(req, next);
            }

            return throwError(error);
          })
        );
      })
    );
  }

  private handle401Error(
    req: HttpRequest<object>,
    next: HttpHandler
  ): Observable<HttpEvent<object>> {
    return from(
      navigator.locks.request(this.REFRESH_LOCK_NAME, () => {
        return this.performTokenRefresh().pipe(take(1)).toPromise();
      })
    ).pipe(
      switchMap((token) => {
        window.sdPortalAuthToken = token;
        return next.handle(this.addAuthHeader(req, token as unknown as string));
      }),
      catchError(error => throwError(error))
    );
  }

  private performTokenRefresh(): Observable<string> {
    return this.oidcSecurityService.forceRefreshSession().pipe(
      take(1),
      switchMap(response => {
        if (response.accessToken) {
          return of(response.accessToken);
        }

        // If no access token, log details and navigate to unauthorized
        Sentry.captureMessage('Token refresh failed: no access token after refresh');

        this.navigateToUnauthorized();
        return throwError(new Error('Token refresh failed: no access token'));
      }),
      catchError(error => {
        Sentry.captureException(error, {
          extra: { context: 'Token refresh error in AuthHttpInterceptor' }
        });

        this.navigateToUnauthorized();
        return throwError(error);
      })
    );
  }

  private addAuthHeader(
    req: HttpRequest<object>,
    token: string
  ): HttpRequest<object> {
    return req.clone({ setHeaders: { Authorization: 'Bearer ' + token } });
  }

  private navigateToUnauthorized(): void {
    // Save url to navigate back after re-authentication
    const currentUrl = this.router.url;
    if (currentUrl && currentUrl !== '/postlogin' && currentUrl !== '/unauthorized' && currentUrl !== '/oidccallback' && currentUrl !== '/') {
      sessionStorage.setItem(SessionStorageKeys.REDIRECT_URL, currentUrl);
    }

    this.router.navigate([this.environment.oidcsettings.unauthorized_route]);
  }
}
